import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { setFileList } from '@sonifi/wb-widget/store/file/fileSlice';
import { GROUPS_KEYS } from '@sonifi/wb-group/store/groupsConstants';
import {
  resetMediaPagination,
  setFolderNode,
  setBrowserTree,
  setLoadingMedia,
  setMediaPagination,
  setSelectAll,
  setNoResults,
  setMediaTypeUpload,
} from '@sonifi/wb-media/store/mediaSlice';
import { proccessAndUploadFiles } from '@sonifi/wb-widget/services/file/file.service';
import {
  MEDIA_KEYS,
  MEDIA_TYPES,
  ROOT_TAG,
  ROOT_TAG_DEFAULT_NAME,
} from '../constants/WbMediaConstants';
import {
  getMediaItemPage,
  getMediaMapped,
  createApiRequestParams,
  processMediaItemsForBrowserTree,
  getMediaKeysForType,
  processMediaByType,
  createMediaUploadBody,
  prepareFilesForUpload,
  createUploadOptions,
  createTagBody,
  createRootTagBody,
  createEditTagBody,
  createMoveMediaBody,
  createMoveFolderBody,
  updateBrowserTreeItemParent,
} from './mediaHelper';
import {
  addNewFolderToTree,
  buildTreeFolders,
  filterBrowserTreeWhenSearch,
  getFolderNodeForGetMedia,
  removeFolderFromTree,
  updateFolderInTree,
  filterBrowserTreeAfterDelete,
  getDefaultMediaTypeForUpload,
  findRootNodeOrDefault,
  shouldCreateRootTag,
  dispatchMediaActions,
} from './mediaBrowserHelper';
import { parseQueryParams } from '@sonifi/wb-core/utils/parseHelper';
import { defaultConfig } from '../../../config-env';

export const deleteMedia =
  ({ mediaSelectorKey, mediaItems }) =>
  async (dispatch, getState) => {
    try {
      const { media: currentMedia } = getState();
      const browserTree = currentMedia[mediaSelectorKey].browserTree;

      const promises = [];
      for (const mediaItem of mediaItems) {
        if (mediaItem.type === 'media') {
          const path = `${mediaItem.data.type}/${mediaItem.data.id}`;
          promises.push(apiService.delete(path));
        } else {
          promises.push(apiService.delete(`${ENDPOINTS.TAGS}/${mediaItem.id}`));
        }
      }

      await Promise.all(promises);

      const ids = mediaItems.map((mi) => mi.data.id);
      const browserTreeUpdated = filterBrowserTreeAfterDelete(browserTree, ids);

      await dispatchMediaActions(dispatch, [
        setBrowserTree({ browserTree: browserTreeUpdated, mediaSelectorKey }),
        setSelectAll({ status: false, mediaSelectorKey }),
      ]);

      return true;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

const processFileUpload = async (filesRes, folderNode, mediaType) => {
  try {
    for (const file of filesRes.accepted) {
      const body = createMediaUploadBody(file, folderNode, defaultConfig);
      await apiService.post(ENDPOINTS[mediaType.key.toUpperCase()], body);
    }
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

const refreshMediaAfterUpload =
  (mediaType, mediaSelectorKey, folderNode) => async (dispatch, getState) => {
    const {
      groups: { selectedGroup },
    } = getState();

    await dispatch(
      getMediaByTags({
        media: mediaType.key,
        groupId: selectedGroup.id,
        mediaSelectorKey,
        node: folderNode.data,
      }),
    );
  };

export const uploadMedia =
  ({ alertId, files, mediaSelectorKey, mediaType }) =>
  async (dispatch, getState) => {
    try {
      // Convert FileList to conventional Array and simple Objects
      dispatch(setFileList(prepareFilesForUpload(files)));

      // First Save Files
      const filesRes = await dispatch(
        proccessAndUploadFiles({
          alertId,
          files: files.accepted,
          options: createUploadOptions(mediaType),
        }),
      );

      // Second: Save files as Media
      const { media: currentMedia } = getState();
      const folderNode = currentMedia[mediaSelectorKey].folderNode;

      await processFileUpload(filesRes, folderNode, mediaType);

      await dispatch(
        refreshMediaAfterUpload(mediaType, mediaSelectorKey, folderNode),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

export const getMediaById = async ({ media, id }) => {
  try {
    const url = `${media}/${id}`;
    const res = await apiService.get(url);
    const group = res.data._embedded[res.data.group];
    const mediaKey = media.toUpperCase();
    const mimetypes = MEDIA_KEYS.MIME[mediaKey];
    const mediaItemMapped = getMediaMapped({
      group,
      media,
      mediaItemsRes: { objects: [res.data], _embedded: res.data._embedded },
      mimetypes,
    })[0];

    return mediaItemMapped;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getLayouts = async () => {
  try {
    const url = `${ENDPOINTS.LAYOUTS}?rpp=-1`;
    const res = await apiService.get(url);
    return res.data.objects;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getTags =
  ({ mediaSelectorKey }) =>
  async (dispatch, getState) => {
    const {
      groups: { selectedGroup },
    } = getState();
    if (!selectedGroup?.uri) return;
    try {
      const res = await apiService.get(
        `${ENDPOINTS.MEDIA_FOLDERS}?group=${selectedGroup.uri}`,
      );
      const folders = res.data.media_folders;
      const browserTree = buildTreeFolders(folders);

      dispatch(setBrowserTree({ browserTree, mediaSelectorKey }));
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

export const createRootTag =
  ({ newTagName, mediaSelectorKey }) =>
  async (dispatch, getState) => {
    const {
      groups: { selectedGroup },
    } = getState();

    try {
      const body = createRootTagBody({
        newTagName,
        group: selectedGroup,
        rootTag: ROOT_TAG,
      });
      const res = await apiService.post(ENDPOINTS.TAGS, body);

      const folders = {
        id: res.data.id,
        name: res.data.tag,
        parent_id: null,
      };
      const browserTree = buildTreeFolders(folders);

      dispatch(setBrowserTree({ browserTree, mediaSelectorKey }));
      return res.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

export const createTag =
  ({ parentId, parentName, tagName, mediaSelectorKey, folderNode }) =>
  async (dispatch, getState) => {
    const {
      groups: { selectedGroup },
      media: currentMedia,
    } = getState();

    const browserTree = currentMedia[mediaSelectorKey].browserTree;
    try {
      const body = createTagBody({
        tagName,
        parentName,
        parentId,
        group: selectedGroup,
        defaultConfig,
      });

      const res = await apiService.post('tags', body);
      const newFolder = {
        id: res.data.id,
        name: body.tag,
        parent_id: parentId,
      };

      const newBrowserTree = addNewFolderToTree(browserTree, newFolder);
      dispatch(
        setBrowserTree({ browserTree: newBrowserTree, mediaSelectorKey }),
      );

      return res.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

export const deleteTag =
  ({ mediaSelectorKey, folderNode }) =>
  async (dispatch, getState) => {
    const { media: currentMedia } = getState();

    const browserTree = currentMedia[mediaSelectorKey].browserTree;

    try {
      await apiService.delete(`${ENDPOINTS.TAGS}/${folderNode.id}`);
      const newBrowserTree = removeFolderFromTree(browserTree, folderNode);
      dispatch(
        setBrowserTree({ browserTree: newBrowserTree, mediaSelectorKey }),
      );
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

export const editTag =
  ({ folderNode, newTagName, mediaSelectorKey }) =>
  async (dispatch, getState) => {
    const { media: currentMedia } = getState();
    const browserTree = currentMedia[mediaSelectorKey].browserTree;

    try {
      const body = createEditTagBody({
        currentFolderName: folderNode.data.name,
        newTagName,
      });
      await apiService.patch(`${ENDPOINTS.TAGS}/${folderNode.id}`, body);

      const newFolder = {
        id: folderNode.id,
        name: body.tag,
        parent_id: folderNode.parent,
      };
      const newBrowserTree = updateFolderInTree(browserTree, newFolder);

      dispatch(
        setBrowserTree({ browserTree: newBrowserTree, mediaSelectorKey }),
      );
      return newFolder;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

// Decide whether another page can be fetched and which page number that is.
// Searches paginate exactly like folder browsing: getMediaItemPage returns
// page 1 for the initial load (pageFromParams === 1) and advances on every
// later page. A term must NOT short-circuit to page 1 here - doing so made
// infinite scroll re-request page 1 forever and duplicate multi-page results.
export const checkPaginationAndGetNext = (currentPagination, page) =>
  getMediaItemPage({
    currentPage: currentPagination.page,
    pagination: currentPagination,
    pageFromParams: page,
  });

const fetchMediaData = async (media, params) => {
  const uri = `${media}?${parseQueryParams(params)}`;
  const res = await apiService.get(uri);
  return res.data;
};

const processMediaResponse = ({ groups, groupId, media, mediaByTags }) => {
  const mediaKeys = getMediaKeysForType(media);
  const allGroups = groups[GROUPS_KEYS.allGroups] || groups;

  return processMediaByType({
    groups: allGroups,
    groupId,
    mediaKeys,
    mediaByTags,
  });
};

// Fetch one node's media page. Resolves the folder node the same way for every
// caller so the request params stay consistent between the initial load and
// incremental folder fetches.
const fetchMediaForNode = async ({
  media,
  browserTree,
  node,
  term,
  selectedGroup,
  page = 1,
  pageSize = 10,
}) => {
  const folderNode = getFolderNodeForGetMedia({ browserTree, node });
  const params = createApiRequestParams({
    page,
    pageSize,
    term,
    selectedGroup,
    folderNode,
    defaultConfig,
    media,
  });
  const mediaByTags = await fetchMediaData(media, params);

  return { folderNode, mediaByTags };
};

// Map one fetch result onto the browser tree and return the new tree. Pure: the
// caller owns the pagination/loading dispatches so it can supply the right page.
const applyMediaResultToTree = ({
  browserTree,
  mediaByTags,
  folderNode,
  term,
  groups,
  groupId,
  media,
}) => {
  const mediaItemsMapped = processMediaResponse({
    groups,
    groupId,
    media,
    mediaByTags,
  });

  return processMediaItemsForBrowserTree({
    mediaItemsMapped,
    term,
    browserTree,
    folderNode,
  });
};

export const getMediaByTags =
  ({
    media,
    groupId,
    mediaSelectorKey,
    node,
    page = 1,
    pageSize = 10,
    term,
    resetBrowserTree = false,
  }) =>
  async (dispatch, getState) => {
    const { media: currentMedia } = getState();
    // The loading flag still serializes droppable fetches (folder select and
    // the scroll sentinel) so their appends can't interleave and the sentinel
    // can't re-fire mid-flight. A user-initiated reset-search
    // (resetBrowserTree) must never be dropped, so it bypasses the guard; a
    // late/stale response is instead discarded at apply time below.
    if (currentMedia[mediaSelectorKey].loading && !resetBrowserTree) {
      return; // already fetching
    }

    // The term this fetch is issued for is its intent. searchText is written
    // synchronously on every keystroke, so comparing the captured term against
    // the live searchText at apply time tells us whether this response is still
    // the one the user wants.
    const intendedSearch = term || '';

    try {
      const {
        media: preMedia,
        groups: { groups, selectedGroup },
      } = getState();

      const browserTree = filterBrowserTreeWhenSearch({
        browserTree: preMedia[mediaSelectorKey].browserTree,
        resetBrowserTree,
      });

      const currentPagination = preMedia[mediaSelectorKey]?.mediaPagination[
        node?.id
      ] || {
        page: 0,
        total: 0,
      };

      const { canNext, nextPage } = checkPaginationAndGetNext(
        currentPagination,
        page,
      );
      if (!canNext) {
        return;
      }

      dispatch(setLoadingMedia({ loading: true, mediaSelectorKey }));

      const { folderNode, mediaByTags } = await fetchMediaForNode({
        media,
        browserTree,
        node,
        term,
        selectedGroup,
        page: nextPage,
        pageSize,
      });

      // Re-read state after the await. If the search intent changed while this
      // request was in flight, this response is stale: discard it so it can't
      // paint browse content over a search, or a superseded search over the
      // current view.
      const { media: postMedia } = getState();
      if (postMedia[mediaSelectorKey].searchText !== intendedSearch) {
        // Stale: the search intent changed while this request was in flight, so
        // drop the response without touching pagination, results, or the tree.
        // Still release the loading claim this fetch took - loading is reference
        // counted, so it only reaches false once the newer reset-search that
        // superseded us has settled as well. That keeps the spinner honest
        // without letting the scroll sentinel fire into a premature gap, and
        // guarantees the flag can never wedge on a discarded response.
        dispatch(setLoadingMedia({ loading: false, mediaSelectorKey }));
        return;
      }

      dispatch(
        setMediaPagination({
          folderKey: term ? 0 : folderNode.id,
          page: nextPage,
          pageSize,
          total: mediaByTags.total,
          mediaSelectorKey,
        }),
      );

      // Merge onto the freshly re-read tree (not the pre-await snapshot) so a
      // valid concurrent append accumulates instead of clobbering.
      const mergeBase = filterBrowserTreeWhenSearch({
        browserTree: postMedia[mediaSelectorKey].browserTree,
        resetBrowserTree,
      });

      const updatedBrowserTree = applyMediaResultToTree({
        browserTree: mergeBase,
        mediaByTags,
        folderNode,
        term,
        groups,
        groupId,
        media,
      });

      if (term) {
        dispatch(
          setNoResults({
            noResults: updatedBrowserTree.length === 0,
            mediaSelectorKey,
          }),
        );
      }

      dispatch(
        setBrowserTree({ browserTree: updatedBrowserTree, mediaSelectorKey }),
      );
      dispatch(setLoadingMedia({ loading: false, mediaSelectorKey }));
    } catch (error) {
      console.error(error);
      dispatch(setLoadingMedia({ loading: false, mediaSelectorKey }));
      return Promise.reject(error);
    }
  };

export const moveMedia =
  ({ mediaSources, folderTarget, mediaSelectorKey }) =>
  async (dispatch, getState) => {
    const { media: currentMedia } = getState();
    const browserTree = currentMedia[mediaSelectorKey].browserTree;

    try {
      const promises = [];
      let newBrowserTree = browserTree;
      for (const mediaSource of mediaSources) {
        const body = createMoveMediaBody(folderTarget, defaultConfig);
        const mediaType = mediaSource.data.type;

        promises.push(
          apiService.patch(
            `${ENDPOINTS[mediaType.toUpperCase()]}/${mediaSource.data.id}`,
            body,
          ),
        );

        newBrowserTree = updateBrowserTreeItemParent({
          browserTree: newBrowserTree,
          source: mediaSource,
          folderTarget,
        });
      }

      dispatch(
        setBrowserTree({ browserTree: newBrowserTree, mediaSelectorKey }),
      );

      await Promise.all(promises);
      return true;
    } catch (error) {
      // Restore previous state
      dispatch(setBrowserTree({ browserTree, mediaSelectorKey }));
      console.error(error);
      return Promise.reject(error);
    }
  };

export const moveFolder =
  ({ folderSource, folderTarget, mediaSelectorKey }) =>
  async (dispatch, getState) => {
    const { media: currentMedia } = getState();
    const browserTree = currentMedia[mediaSelectorKey].browserTree;

    try {
      const body = createMoveFolderBody({
        folderSource,
        folderTarget,
        defaultConfig,
        endpoints: ENDPOINTS,
      });

      await apiService.patch(`${ENDPOINTS.TAGS}/${folderSource.id}`, body);

      const newBrowserTree = updateBrowserTreeItemParent({
        browserTree,
        source: folderSource,
        folderTarget,
      });

      dispatch(
        setBrowserTree({ browserTree: newBrowserTree, mediaSelectorKey }),
      );
      return true;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

const initializeBrowserTags = (mediaSelectorKey) => async (dispatch) => {
  await dispatch(getTags({ mediaSelectorKey }));
};

const createRootTagIfNeeded =
  (browserTree, mediaSelectorKey) => async (dispatch) => {
    if (shouldCreateRootTag(browserTree)) {
      await dispatch(
        createRootTag({
          newTagName: ROOT_TAG_DEFAULT_NAME,
          mediaSelectorKey,
        }),
      );
    }
  };

const setupMediaBrowser =
  (browserTree, folderNode, mediaType, mediaSelectorKey) => (dispatch) => {
    const nodeSelected = findRootNodeOrDefault(browserTree, folderNode);

    dispatch(setFolderNode({ node: nodeSelected, mediaSelectorKey }));

    const mediaTypeUpload = getDefaultMediaTypeForUpload(
      mediaType,
      MEDIA_TYPES,
      MEDIA_KEYS,
    );
    dispatch(setMediaTypeUpload({ mediaTypeUpload, mediaSelectorKey }));

    return nodeSelected;
  };

export const initMediaBrowser =
  ({ group, mediaSelectorKey, mediaType, folderNode, searchText }) =>
  async (dispatch, getState) => {
    // The search this init is for. If it changes while init is in flight,
    // init's browse writes become stale (see the apply-time guard below).
    const intendedSearch = searchText || '';
    try {
      dispatch(resetMediaPagination({ mediaSelectorKey }));

      await dispatch(initializeBrowserTags(mediaSelectorKey));

      // Create root tag if not exist
      const { media: currentMediaInitial } = getState();
      const browserTreeInitial =
        currentMediaInitial[mediaSelectorKey].browserTree;

      await dispatch(
        createRootTagIfNeeded(browserTreeInitial, mediaSelectorKey),
      );

      const {
        media: currentMedia,
        groups: { groups, selectedGroup },
      } = getState();
      const browserTree = currentMedia[mediaSelectorKey].browserTree;

      const nodeSelected = dispatch(
        setupMediaBrowser(browserTree, folderNode, mediaType, mediaSelectorKey),
      );

      // Build the full set of nodes needing media: root first, then any
      // previously expanded non-root folders. While searching, results are
      // flat at the root and the folder filter is ignored, so a single request
      // is all that is meaningful — fetching per expanded folder would issue
      // identical queries and duplicate the same results into the tree.
      const expandedNodes = currentMedia[mediaSelectorKey].expandedNodes;
      const nodesToFetch = searchText
        ? [nodeSelected]
        : [
            nodeSelected,
            ...expandedNodes
              .filter((id) => id !== nodeSelected?.id)
              .map((id) => browserTree.find((n) => n.id === id))
              .filter(Boolean),
          ];

      dispatch(setLoadingMedia({ loading: true, mediaSelectorKey }));

      // Fire all HTTP requests concurrently. Use allSettled so one stale or
      // deleted folder tag cannot reject the whole batch and discard the media
      // that did load (including the root's).
      const settled = await Promise.allSettled(
        nodesToFetch.map((node) =>
          fetchMediaForNode({
            media: mediaType.key,
            browserTree,
            node,
            term: searchText,
            selectedGroup,
          }),
        ),
      );

      const fetchResults = settled
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);

      // Only treat the load as failed when every request failed; otherwise
      // render whatever succeeded.
      if (fetchResults.length === 0) {
        throw settled[0]?.reason ?? new Error('Failed to load media');
      }

      // If a search was typed while this init was in flight, its browse writes
      // are stale - discard them so they cannot paint over the search results.
      const { media: postMedia } = getState();
      if (postMedia[mediaSelectorKey].searchText !== intendedSearch) {
        return;
      }

      // Apply every result onto one accumulating tree, then write once.
      let updatedTree = browserTree;
      for (const { folderNode: fetchedFolderNode, mediaByTags } of fetchResults) {
        dispatch(
          setMediaPagination({
            folderKey: searchText ? 0 : fetchedFolderNode.id,
            page: 1,
            pageSize: 10,
            total: mediaByTags.total,
            mediaSelectorKey,
          }),
        );

        updatedTree = applyMediaResultToTree({
          browserTree: updatedTree,
          mediaByTags,
          folderNode: fetchedFolderNode,
          term: searchText,
          groups,
          groupId: group.id,
          media: mediaType.key,
        });
      }

      if (searchText) {
        dispatch(
          setNoResults({
            noResults: updatedTree.length === 0,
            mediaSelectorKey,
          }),
        );
      }

      dispatch(setBrowserTree({ browserTree: updatedTree, mediaSelectorKey }));
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    } finally {
      // Always release this init's claim on the loading flag, on every exit path
      // (success, stale discard, or error). Leaving it held wedges the browser:
      // getMediaByTags early-returns while loading is true, so every later
      // folder click and pagination would silently no-op until a remount.
      // Releasing is safe even when a concurrent reset-search is still running -
      // loading is reference counted, so it stays true until that fetch settles
      // too.
      dispatch(setLoadingMedia({ loading: false, mediaSelectorKey }));
    }
  };
